import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Award, Clock, Target } from "lucide-react";
import { Link } from "react-router-dom";

const CyberEssentialsCertificationService = () => {
  const localAreas = ["Somerset", "North Somerset", "Weston-super-Mare", "Bristol", "South Gloucestershire"];
  
  return (
    <div className="min-h-screen">
      <SEO
        title="Cyber Essentials Certification UK | Get Certified in 4-6 Weeks | 98% Pass Rate | 4FOX"
        description="Expert Cyber Essentials certification support for Somerset, Bristol & UK businesses. 98% first-time pass rate. Required for government contracts. Get certified from £995. Readiness check available."
        canonical="https://4fox.uk/services/cyber-essentials-certification-uk"
        localAreas={localAreas}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ProfessionalService",
              "name": "Cyber Essentials Certification",
              "provider": {
                "@type": "Organisation",
                "name": "4FOX Security"
              },
              "serviceType": "Cyber Essentials Certification Support",
              "areaServed": [
                { "@type": "Country", "name": "United Kingdom" },
                ...localAreas.map(area => ({ "@type": "City", "name": area }))
              ],
              "priceRange": "££",
              "serviceOutput": "Cyber Essentials Certification"
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "How much does Cyber Essentials certification cost?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Cyber Essentials certification typically costs from £995, which includes gap analysis, remediation support, application completion, and submission guidance."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How long does Cyber Essentials certification take?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "The certification process typically takes 4-6 weeks from initial engagement to certification, including gap analysis, remediation, and submission."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Is Cyber Essentials mandatory for government contracts?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, Cyber Essentials certification is required for UK government contracts involving the handling of sensitive and personal information, or providing certain ICT products or services."
                  }
                }
              ]
            }
          ]
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <Award className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Cyber Essentials Certification UK</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Get certified in 4-6 weeks with expert support. 98% first-time pass rate for Somerset, Bristol, and UK businesses.
            </p>

            {/* Trust Signals Bar */}
            <div className="grid md:grid-cols-3 gap-4 mb-12">
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-accent">98%</div>
                <div className="text-sm text-muted-foreground">First-Time Pass Rate</div>
              </div>
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-accent">4-6</div>
                <div className="text-sm text-muted-foreground">Weeks to Certification</div>
              </div>
              <div className="bg-accent/10 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-accent">100+</div>
                <div className="text-sm text-muted-foreground">Businesses Certified</div>
              </div>
            </div>

            {/* In a Nutshell */}
            <div className="bg-primary/5 border-l-4 border-accent rounded-lg p-6 mb-8">
              <h2 className="text-2xl font-bold mb-3">In a Nutshell</h2>
              <p className="text-muted-foreground">
                Cyber Essentials is a UK government-backed certification scheme that protects your organisation against 80% of common cyber attacks. It's a requirement for government contracts and demonstrates your commitment to cyber security. We guide you through the entire process, from gap analysis to certification, with a proven 98% first-time pass rate.
              </p>
            </div>

            <div className="prose prose-lg max-w-none mb-12">
              {/* Why Get Certified */}
              <h2 className="text-2xl font-bold mb-4">Why Get Certified?</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Required for UK government contracts</strong>
                    <p className="text-muted-foreground text-sm">Mandatory for contracts handling sensitive data</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Cyber insurance discounts</strong>
                    <p className="text-muted-foreground text-sm">10-15% reduction in premiums reported</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Competitive advantage</strong>
                    <p className="text-muted-foreground text-sm">Win more bids with security credentials</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <div>
                    <strong>Customer confidence</strong>
                    <p className="text-muted-foreground text-sm">Demonstrate commitment to security</p>
                  </div>
                </div>
              </div>

              {/* Certification Process */}
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Clock className="w-6 h-6 text-accent" />
                Our Certification Process
              </h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="mb-4"><strong>4-6 Week Timeline:</strong></p>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">1</div>
                    <div>
                      <strong>Week 1-2: Gap Analysis & Readiness Assessment</strong>
                      <p className="text-muted-foreground">Comprehensive review of all five Cyber Essentials controls. Identify gaps and create remediation plan.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">2</div>
                    <div>
                      <strong>Week 3-4: Remediation & Evidence Gathering</strong>
                      <p className="text-muted-foreground">Fix identified gaps with hands-on support. Document compliance evidence across your IT environment.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center text-accent font-bold">3</div>
                    <div>
                      <strong>Week 5-6: Application & Certification</strong>
                      <p className="text-muted-foreground">Complete and submit application. Liaise with certification body. Achieve certification.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What's Included */}
              <h2 className="text-2xl font-bold mb-4">What's Included</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Initial gap analysis</strong> across all five Cyber Essentials controls</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Remediation roadmap</strong> with prioritised actions and timelines</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Hands-on implementation support</strong> for addressing identified gaps</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Application completion</strong> with expert guidance through the self-assessment</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Submission support</strong> and liaison with certification body</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Evidence documentation</strong> for all required controls</span>
                </li>
              </ul>

              {/* The 5 Controls */}
              <h2 className="text-2xl font-bold mb-4">The 5 Cyber Essentials Controls</h2>
              <p className="mb-4">We ensure compliance across all five technical controls:</p>
              <div className="space-y-3 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">1. Firewalls</strong>
                  <p className="text-muted-foreground text-sm mt-1">Configure and document network security boundaries</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">2. Secure Configuration</strong>
                  <p className="text-muted-foreground text-sm mt-1">Harden systems and remove unnecessary services</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">3. User Access Control</strong>
                  <p className="text-muted-foreground text-sm mt-1">Implement proper authentication and authorization</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">4. Malware Protection</strong>
                  <p className="text-muted-foreground text-sm mt-1">Deploy and maintain anti-malware solutions</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong className="text-accent">5. Patch Management</strong>
                  <p className="text-muted-foreground text-sm mt-1">Ensure timely security updates across all systems</p>
                </div>
              </div>

              {/* Common Gaps We Address */}
              <h2 className="text-2xl font-bold mb-4">Common Gaps We Address</h2>
              <p className="mb-4">Most organisations need support with:</p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <Target className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Patch management automation:</strong> Setting up processes for timely security updates</span>
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Firewall rule documentation:</strong> Properly documenting network access controls</span>
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Access control reviews:</strong> Implementing MFA and user access management</span>
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Mobile device management:</strong> Securing BYOD and mobile devices</span>
                </li>
                <li className="flex items-start gap-2">
                  <Target className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Security configuration:</strong> Hardening Windows, macOS, and cloud services</span>
                </li>
              </ul>

              {/* CE vs CE Plus */}
              <h2 className="text-2xl font-bold mb-4">Cyber Essentials vs Cyber Essentials Plus</h2>
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-secondary/20">
                      <th className="border border-border p-3 text-left">Feature</th>
                      <th className="border border-border p-3 text-center">Cyber Essentials</th>
                      <th className="border border-border p-3 text-center">Cyber Essentials Plus</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-border p-3">Self-assessment questionnaire</td>
                      <td className="border border-border p-3 text-center">✓</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr className="bg-secondary/10">
                      <td className="border border-border p-3">Technical verification scan</td>
                      <td className="border border-border p-3 text-center">-</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Hands-on testing</td>
                      <td className="border border-border p-3 text-center">-</td>
                      <td className="border border-border p-3 text-center">✓</td>
                    </tr>
                    <tr className="bg-secondary/10">
                      <td className="border border-border p-3">Typical timeline</td>
                      <td className="border border-border p-3 text-center">4-6 weeks</td>
                      <td className="border border-border p-3 text-center">6-8 weeks</td>
                    </tr>
                    <tr>
                      <td className="border border-border p-3">Best for</td>
                      <td className="border border-border p-3 text-center">Most organisations</td>
                      <td className="border border-border p-3 text-center">Higher assurance needs</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Industries We've Certified */}
              <h2 className="text-2xl font-bold mb-4">Industries We've Certified</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Financial Services</strong>
                  <p className="text-muted-foreground text-sm">Banks, insurance, fintech, accountancy</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Healthcare</strong>
                  <p className="text-muted-foreground text-sm">NHS suppliers, private clinics, care providers</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Manufacturing</strong>
                  <p className="text-muted-foreground text-sm">Industrial, automotive, aerospace</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Professional Services</strong>
                  <p className="text-muted-foreground text-sm">Legal, consulting, marketing, IT services</p>
                </div>
              </div>

              {/* FAQ Section */}
              <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4 mb-6">
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>How much does Cyber Essentials cost?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Certification typically costs from £995, which includes gap analysis, remediation support, application completion, and submission. Cyber Essentials Plus costs more due to technical verification requirements.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>How long does certification take?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Most organisations achieve certification in 4-6 weeks with our support, depending on the complexity of your IT environment and the gaps identified during assessment.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>What's the difference between CE and CE Plus?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Cyber Essentials involves a self-assessment questionnaire, while CE Plus includes technical verification scans and hands-on testing. CE is suitable for most organisations; CE Plus provides higher assurance for organisations with specific requirements.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Do I need Cyber Essentials for government contracts?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Yes, Cyber Essentials is mandatory for UK government contracts involving the handling of sensitive and personal information, or providing certain ICT products or services.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Can you help with annual recertification?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Absolutely. We provide ongoing recertification support to ensure you maintain compliance year after year. Many of our clients use us for annual renewals.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>What are the 5 Cyber Essentials controls?</strong>
                  <p className="text-muted-foreground text-sm mt-2">The five controls are: Firewalls, Secure Configuration, User Access Control, Malware Protection, and Patch Management. Together, these prevent approximately 80% of common cyber attacks.</p>
                </div>
                <div className="bg-secondary/20 rounded-lg p-4">
                  <strong>Will it reduce my cyber insurance premiums?</strong>
                  <p className="text-muted-foreground text-sm mt-2">Many insurers offer 10-15% discounts for Cyber Essentials certified organisations. Check with your insurance provider for specific details.</p>
                </div>
              </div>

              {/* Local Targeting */}
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold mb-2">Serving Somerset, Bristol & Beyond</h3>
                <p className="text-muted-foreground">
                  We support businesses across Somerset, North Somerset, Weston-super-Mare, Bristol, South Gloucestershire, and throughout the UK with expert Cyber Essentials certification services.
                </p>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to get certified?</h3>
              <p className="text-muted-foreground mb-6">
                Start with a readiness assessment to understand where you stand, or book a consultation to begin your certification journey.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services/cyber-essentials-readiness">
                  <Button size="lg">
                    Readiness Assessment
                  </Button>
                </Link>
                <a 
                  href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button size="lg" className="bg-accent hover:bg-accent/90">
                    Book Certification Consultation
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CyberEssentialsCertificationService;